import { useRouter } from "next/router";

import { useAuth } from "@/auth";
import {
  CreateSavedSearchBody,
  PatchSavedSearchBody,
  SAVED_SEARCHES_QUERY_KEY,
  createSavedSearch,
  deleteSavedSearch,
  patchSavedSearch,
  savedSearchesQueryOptions,
} from "@/features/search/api/saved-searches";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

/** Every mutation lands on the same list, so they all invalidate the same root. */
const useInvalidateSavedSearches = () => {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({ queryKey: SAVED_SEARCHES_QUERY_KEY });
};

/**
 * The caller's saved searches, one page at a time.
 *
 * Returns an empty list rather than erroring while signed out — the query simply never runs
 * without a token, which is what every consumer wants (the account page is behind auth, and the
 * header entry just renders nothing).
 *
 * The page lives in `?page=n`, like every other paged list on the site. That is also why deleting
 * an entry on page 2 stays on page 2: nothing about the page number is component state.
 */
export const useSavedSearches = () => {
  const { token } = useAuth();
  const { locale, query } = useRouter();

  /* `?page` is user input: anything unparseable, zero or negative reads as page 1. */
  const page = Math.max(1, Math.trunc(Number(query.page)) || 1);

  return useQuery(savedSearchesQueryOptions(token, locale, page));
};

/**
 * Save the current search. Takes the query string as-is — see `createSavedSearch` for why nothing
 * needs translating on the way in.
 */
export const useCreateSavedSearch = () => {
  const { token } = useAuth();
  const invalidate = useInvalidateSavedSearches();

  return useMutation({
    mutationFn: (body: CreateSavedSearchBody) =>
      createSavedSearch(token as string, body),
    onSuccess: invalidate,
  });
};

/** Rename a saved search, or turn its alerts on and off. */
export const usePatchSavedSearch = () => {
  const { token } = useAuth();
  const invalidate = useInvalidateSavedSearches();

  return useMutation({
    mutationFn: ({ id, ...body }: PatchSavedSearchBody & { id: string }) =>
      patchSavedSearch(token as string, id, body),
    onSuccess: invalidate,
  });
};

export const useDeleteSavedSearch = () => {
  const { token } = useAuth();
  const invalidate = useInvalidateSavedSearches();

  return useMutation({
    mutationFn: (id: string) => deleteSavedSearch(token as string, id),
    onSuccess: invalidate,
  });
};
